import { z } from 'zod/v4';

import { BareCpnIdentifierRuntype, BareIpnIdentifierRuntype, BomItemsRuntype } from '../assembly';
import { ComplianceStatusRuntype, CurrencyRuntype } from '../backendTypes';
import { DesignItemResponseDTONewRuntype } from '../designItem';
import {
    CustomComponentFullRuntype,
    OtsComponentFullRuntype,
} from '../internalPartNumber/internalPartNumberBackendTypes';
import { LifecycleEnum, OtsFullPartRuntype, PackageDTORuntype } from '../part';
import { SupplierDTORuntype } from '../supplier';
import { SupplierAndStockLocationDTORuntype } from '../supplierAndStockLocation';
import { TimeSeriesV2Runtype } from './timeSeriesSchema';

export type SupplierAggregationRow = z.infer<typeof SupplierAggregationRowSchema>;
export const SupplierAggregationRowSchema = z.object({
    /** Composite key: `${supplier_id}__${stock_location_id}` */
    id: z.string(),
    supplier: SupplierAndStockLocationDTORuntype,
    unit_price_1: z.number().nullable(),
    unit_price_100: z.number().nullable(),
    unit_price_1k: z.number().nullable(),
    unit_price_10k: z.number().nullable(),
    unit_price_50k: z.number().nullable(),
    /** AVG or MIN stock across all offers in the time window (controlled by `aggregation`) */
    total_stock: z.number().nullable(),
    /** AVG or MIN lead time in days across all offers in the time window (controlled by `aggregation`) */
    lead_time_days: z.number().nullable(),
    offer_count: z.number(),
});

export const OfferValidityRuntype = z.enum(['valid', 'suspicious', 'invalid']);
export type OfferValidity = z.infer<typeof OfferValidityRuntype>;

export const OfferClassificationRuleRuntype = z.enum(['manual', 'median_trim', 'supplier_denylist', 'sanity_bounds']);
export type OfferClassificationRule = z.infer<typeof OfferClassificationRuleRuntype>;

export type HistoricalOfferDTO = z.infer<typeof HistoricalOfferDTOSchema>;
export const HistoricalOfferDTOSchema = z.object({
    time: z.date(), // Timestamp string from database
    part: OtsFullPartRuntype,
    stock_location: SupplierAndStockLocationDTORuntype,
    origin: z.string(),
    price_type: z.string(),
    total_stock: z.number(),
    lead_time_days: z.number().nullable(),
    unit_price_1: z.number().nullable(),
    unit_price_100: z.number().nullable(),
    unit_price_1k: z.number().nullable(),
    unit_price_10k: z.number().nullable(),
    unit_price_50k: z.number().nullable(),
    original_currency: CurrencyRuntype,
    id: z.uuid(),
    packaging: z.string().nullable(),
    validity: OfferValidityRuntype,
    classification_rule: OfferClassificationRuleRuntype.nullable(),
    classified_by_user_id: z.uuid().nullable(),
});

export function ValidatedSchemaOf<T>(schema: z.ZodSchema<T>) {
    return z.union([
        z.object({
            status: z.literal('success'),
            value: schema,
            message: z.string().optional(),
        }),
        z.object({
            status: z.literal('error'),
            message: z.string(),
            value: schema.optional(),
        }),
        z.object({
            status: z.literal('pending'),
            value: schema.optional(),
            message: z.string().optional(),
        }),
    ]);
}

export { TimeSeriesV2Runtype, type TimeSeriesV2 } from './timeSeriesSchema';

/**
 * Sparse date→value map. Kept only as a non-wire type for the DB-migration layer
 * (`mapToTimeSeriesV2` up-converts old stored series) and for local conversions
 * such as `TimeSeries.toMap()`. The wire/storage format is the dense
 * {@link TimeSeriesV2Runtype}; sparse series never cross the network.
 */
export type SparseTimeSeries = Map<string, number | null>;

/**
 * Stable diagnostic bits for current-spend-baseline data-quality issues.
 *
 * These values are stored in `TimeSeriesV2.issues` as a bit mask, so do not
 * change existing bit positions. Add new diagnostics with the next available
 * bit instead.
 */
export const BaselineDiagnosticBit = {
    DemandFillForwarded: 1 << 0,
    DemandMissingZeroUsed: 1 << 1,
    PurchasePriceFillForwarded: 1 << 2,
    PurchasePriceMissingMarketFallbackUsed: 1 << 3,
    PurchasePriceMissingZeroUsed: 1 << 4,
} as const;
export type BaselineDiagnosticBit = (typeof BaselineDiagnosticBit)[keyof typeof BaselineDiagnosticBit];

export const BaselineDiagnosticBitRuntype = z.enum(BaselineDiagnosticBit);

export const DiagnosticSeverityRuntype = z.enum(['warning', 'error']);
export type DiagnosticSeverity = z.infer<typeof DiagnosticSeverityRuntype>;

const ValidatedTimeSeriesV2Runtype = ValidatedSchemaOf(TimeSeriesV2Runtype);
export type ValidatedTimeSeriesV2 = z.infer<typeof ValidatedTimeSeriesV2Runtype>;

/** A concrete issue found while explaining diagnostics for a requested scope. */
export const DiagnosticIssueRuntype = z.object({
    /** Node affected by this issue. */
    nodeId: z.lazy(() => CalculationNodeIdRuntype),

    /** Well-known issue bit. */
    issueId: BaselineDiagnosticBitRuntype,
});
export type DiagnosticIssue = z.infer<typeof DiagnosticIssueRuntype>;

/**
 * Request to explain diagnostics for one node, one attribute, and one date window.
 *
 * Example: explain `price_index` for `PartCategory:5` at the beginning of last year.
 */
export const DiagnosticDetailsRequestRuntype = z.object({
    /** Calculation node to explain. */
    nodeId: z.lazy(() => CalculationNodeIdRuntype),

    /** Inclusive/exclusive interpretation is decided by the endpoint implementation. */
    dateWindow: z.object({
        from: z.iso.date(),
        to: z.iso.date(),
    }),

    /** Attribute/metric to explain, e.g. `current_spend_baseline` or `price_index`. */
    attribute: z.string(),
});
export type DiagnosticDetailsRequest = z.infer<typeof DiagnosticDetailsRequestRuntype>;

export const DiagnosticDetailsResponseRuntype = z.object({
    /** Offer/source price records used by the explained calculation, including fill-forwarded sources. */
    offerIds: z.array(z.string()),

    /** Demand records used by the explained calculation, including fill-forwarded sources. */
    demandIds: z.array(z.string()),

    /** Typed issues observed in the requested diagnostic scope. */
    issues: z.array(DiagnosticIssueRuntype),
});
export type DiagnosticDetailsResponse = z.infer<typeof DiagnosticDetailsResponseRuntype>;

// Fully typed calculated field with time-series AND derived sortable values
export type Calculated = z.infer<typeof CalculatedRuntype>;
export const CalculatedRuntype = z.object({
    // Full time-series (for charts/display)
    price_index: ValidatedTimeSeriesV2Runtype.optional(),
    price_index_10k: ValidatedTimeSeriesV2Runtype.optional(),
    price_index_50k: ValidatedTimeSeriesV2Runtype.optional(),
    price_index_p10: ValidatedTimeSeriesV2Runtype.optional(),
    price_index_p90: ValidatedTimeSeriesV2Runtype.optional(),
    stock_index: ValidatedTimeSeriesV2Runtype.optional(),
    lead_time_index: ValidatedTimeSeriesV2Runtype.optional(),
    purchase_price_index: ValidatedTimeSeriesV2Runtype.optional(),
    contract_price_index: ValidatedTimeSeriesV2Runtype.optional(),
    quote_price_index: ValidatedTimeSeriesV2Runtype.optional(),
    market_unit_price_1k_p50: ValidatedTimeSeriesV2Runtype.optional(),
    number_of_offers: ValidatedTimeSeriesV2Runtype.optional(),
    pct_missing_offers: ValidatedTimeSeriesV2Runtype.optional(),
    savings: ValidatedTimeSeriesV2Runtype.optional(),

    // Non-sortable time-series (stored as attributes but not in SORTABLE_ATTRIBUTES)
    count_parts: ValidatedTimeSeriesV2Runtype.optional(),

    demand_quantity: ValidatedTimeSeriesV2Runtype.optional(),
    current_spend_baseline: ValidatedTimeSeriesV2Runtype.optional(),

    // Derived sortable values - _last (final value in time-series)
    price_index_last: z.number().nullable().optional(),
    price_index_10k_last: z.number().nullable().optional(),
    price_index_50k_last: z.number().nullable().optional(),
    price_index_p10_last: z.number().nullable().optional(),
    price_index_p90_last: z.number().nullable().optional(),
    stock_index_last: z.number().nullable().optional(),
    lead_time_index_last: z.number().nullable().optional(),
    purchase_price_index_last: z.number().nullable().optional(),
    contract_price_index_last: z.number().nullable().optional(),
    quote_price_index_last: z.number().nullable().optional(),
    number_of_offers_last: z.number().nullable().optional(),
    pct_missing_offers_last: z.number().nullable().optional(),
    savings_last: z.number().nullable().optional(),
    count_parts_last: z.number().nullable().optional(),
    demand_quantity_last: z.number().nullable().optional(),
    current_spend_baseline_last: z.number().nullable().optional(),

    // Derived sortable values - _pct_change (Final / Initial - 1)
    price_index_pct_change: z.number().nullable().optional(),
    price_index_10k_pct_change: z.number().nullable().optional(),
    price_index_50k_pct_change: z.number().nullable().optional(),
    price_index_p10_pct_change: z.number().nullable().optional(),
    price_index_p90_pct_change: z.number().nullable().optional(),
    stock_index_pct_change: z.number().nullable().optional(),
    lead_time_index_pct_change: z.number().nullable().optional(),
    purchase_price_index_pct_change: z.number().nullable().optional(),
    contract_price_index_pct_change: z.number().nullable().optional(),
    quote_price_index_pct_change: z.number().nullable().optional(),
    number_of_offers_pct_change: z.number().nullable().optional(),
    pct_missing_offers_pct_change: z.number().nullable().optional(),
    savings_pct_change: z.number().nullable().optional(),
    demand_quantity_pct_change: z.number().nullable().optional(),
    current_spend_baseline_pct_change: z.number().nullable().optional(),
});

// Keep CalculatedValue for backward compatibility
export type CalculatedValue = z.infer<typeof CalculatedValueRuntype>;
const CalculatedValueRuntype = ValidatedSchemaOf(z.unknown());

export type CalculationNodeType = z.infer<typeof CalculationNodeTypeRuntype>;
export const CalculationNodeTypeRuntype = z.union([
    z.literal('PartCategory'),
    z.literal('OffTheShelfPart'),
    z.literal('OtsComponent'),
    z.literal('CustomComponent'),
    z.literal('BomItem'),
    z.literal('Manufacturer'),
    z.literal('PartLibrary'),
    z.literal('ComponentLibrary'),
    z.literal('HistoricalOffer'),
    z.literal('Supplier'),
    z.literal('SupplierManufacturer'),
    z.literal('SupplierPart'),
    z.literal('SupplierLibrary'),
    z.literal('RiskAllAssemblies'),
    z.literal('RiskAssembly'),
    z.literal('RiskOtsComponent'),
    z.literal('RiskOtsPart'),
    z.literal('RiskBomItems'),
]);

export type CalculationNodeId = z.infer<typeof CalculationNodeIdRuntype>;
export const CalculationNodeIdRuntype = z.object({
    type: CalculationNodeTypeRuntype,
    id: z.string(),
});

export const SUPPLIER_MANUFACTURER_ID_SEPARATOR = '|';

export function toSupplierManufacturerId({
    supplierId,
    manufacturerId,
}: {
    supplierId: string;
    manufacturerId: string;
}): string {
    return `${supplierId}${SUPPLIER_MANUFACTURER_ID_SEPARATOR}${manufacturerId}`;
}

export function parseSupplierManufacturerId(id: string): { supplierId: string; manufacturerId: string } | undefined {
    const index = id.indexOf(SUPPLIER_MANUFACTURER_ID_SEPARATOR);
    if (index <= 0 || index === id.length - 1) {
        return undefined;
    }
    return { supplierId: id.slice(0, index), manufacturerId: id.slice(index + 1) };
}

const ParentRefRuntype = z.object({ type: z.string(), id: z.string() }).optional();

const CalculationNodeManufacturerPayloadRuntype = z.object({
    id: z.string(),
    name: z.string(),
});

const CalculationNodePartCategoryPayloadRuntype = z
    .object({
        part_category: z.object({
            id: z.number(),
            name: z.string(),
        }),
        path: z.array(z.number()),
    })
    .nullable()
    .optional();

const CalculationNodeOffTheShelfPartPayloadRuntype = z.object({
    id: z.string(),
    manufacturer: CalculationNodeManufacturerPayloadRuntype,
    mpn: z.string(),
    part_category: CalculationNodePartCategoryPayloadRuntype,
    description: z.string().nullable(),
    datasheet_url: z.string().nullable(),
    image_url: z.string().nullable(),
    package: PackageDTORuntype.nullable(),
    hts_code: z.string().nullable(),
    lifecycle_status: z.enum(LifecycleEnum),
    lifecycle_yteol: z.number().nullable(),
    lifecycle_yteol_range: z.string().nullable(),
    country_of_origin: z.array(z.string()),
    reach_compliant: ComplianceStatusRuntype,
    rohs_compliant: ComplianceStatusRuntype,
    aecq_compliant: ComplianceStatusRuntype,
    china_rohs_compliant: ComplianceStatusRuntype,
    pfas_compliant: ComplianceStatusRuntype,
    prop65_compliant: ComplianceStatusRuntype,
    ipns: z.array(z.string()).optional(),
});

export type CalculationNodeOtsComponent = z.infer<typeof CalculationNodeOtsComponentRuntype>;
export const CalculationNodeOtsComponentRuntype = z.object({
    type: z.literal('OtsComponent'),
    id: z.string(),
    component: OtsComponentFullRuntype.pick({
        id: true,
        category: true,
        state: true,
        part_specifications: true,
    }),
    countOfParts: z.number().nullable().optional(),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(CalculationNodeOffTheShelfPartRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeCustomComponent = z.infer<typeof CalculationNodeCustomComponentRuntype>;
export const CalculationNodeCustomComponentRuntype = z.object({
    type: z.literal('CustomComponent'),
    id: z.string(),
    component: CustomComponentFullRuntype.pick({
        id: true,
        description: true,
        category: true,
        state: true,
    }),
    calculated: CalculatedRuntype,
    children: z.tuple([]),
    parent: ParentRefRuntype,
});

const RiskDataRuntype = z.object({
    rohs_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    rohs_non_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    rohs_unknown_count: ValidatedSchemaOf(z.number()).optional(),
    reach_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    reach_non_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    reach_unknown_count: ValidatedSchemaOf(z.number()).optional(),
    aecq_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    aecq_non_compliant_count: ValidatedSchemaOf(z.number()).optional(),
    aecq_unknown_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_pre_release_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_active_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_unknown_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_acquired_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_nrnd_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_eol_count: ValidatedSchemaOf(z.number()).optional(),
    lifecycle_obsolete_count: ValidatedSchemaOf(z.number()).optional(),

    // Never set; declared so the price fields can be read off any node's `calculated`.
    price_index: z.undefined().optional(),
    price_index_10k: z.undefined().optional(),
    price_index_50k: z.undefined().optional(),
    stock_index: z.undefined().optional(),
    lead_time_index: z.undefined().optional(),
    purchase_price_index: z.undefined().optional(),
    contract_price_index: z.undefined().optional(),
    quote_price_index: z.undefined().optional(),
    number_of_offers: z.undefined().optional(),
    pct_missing_offers: z.undefined().optional(),
    demand_quantity: z.undefined().optional(),
    current_spend_baseline: z.undefined().optional(),

    price_index_last: z.undefined().optional(),
    price_index_10k_last: z.undefined().optional(),
    price_index_50k_last: z.undefined().optional(),
    stock_index_last: z.undefined().optional(),
    lead_time_index_last: z.undefined().optional(),
    purchase_price_index_last: z.undefined().optional(),
    contract_price_index_last: z.undefined().optional(),
    quote_price_index_last: z.undefined().optional(),
    number_of_offers_last: z.undefined().optional(),
    pct_missing_offers_last: z.undefined().optional(),
    demand_quantity_last: z.undefined().optional(),
    current_spend_baseline_last: z.undefined().optional(),

    price_index_pct_change: z.undefined().optional(),
    price_index_10k_pct_change: z.undefined().optional(),
    price_index_50k_pct_change: z.undefined().optional(),
    stock_index_pct_change: z.undefined().optional(),
    lead_time_index_pct_change: z.undefined().optional(),
    purchase_price_index_pct_change: z.undefined().optional(),
    contract_price_index_pct_change: z.undefined().optional(),
    quote_price_index_pct_change: z.undefined().optional(),
    number_of_offers_pct_change: z.undefined().optional(),
    pct_missing_offers_pct_change: z.undefined().optional(),
    demand_quantity_pct_change: z.undefined().optional(),
    current_spend_baseline_pct_change: z.undefined().optional(),
});

export type CalculationNodeRiskOtsPart = z.infer<typeof CalculationNodeRiskOtsPartRuntype>;
export const CalculationNodeRiskOtsPartRuntype = z.object({
    type: z.literal('RiskOtsPart'),
    id: z.string(),
    children: z.tuple([]),
    calculated: RiskDataRuntype,
    reach_compliant: ComplianceStatusRuntype,
    rohs_compliant: ComplianceStatusRuntype,
    aecq_compliant: ComplianceStatusRuntype,
    lifecycle: z.enum(LifecycleEnum),
    parent: ParentRefRuntype,
});

export type CalculationNodeRiskOtsComponent = z.infer<typeof CalculationNodeRiskOtsComponentRuntype>;
export const CalculationNodeRiskOtsComponentRuntype = z.object({
    type: z.literal('RiskOtsComponent'),
    id: z.string(),
    children: z.array(CalculationNodeRiskOtsPartRuntype),
    calculated: RiskDataRuntype,
    parent: ParentRefRuntype,
});

export type CalculationNodeRiskBomItems = z.infer<typeof CalculationNodeRiskBomItemsRuntype>;
export const CalculationNodeRiskBomItemsRuntype = z.object({
    type: z.literal('RiskBomItems'),
    id: z.string(),
    children: z.array(z.union([CalculationNodeRiskOtsComponentRuntype, CalculationNodeRiskOtsPartRuntype])),
    calculated: RiskDataRuntype,
    parent: ParentRefRuntype,
});

export type CalculationNodeRiskAssembly = z.infer<typeof CalculationNodeRiskAssemblyRuntype>;
export const CalculationNodeRiskAssemblyRuntype = z.object({
    type: z.literal('RiskAssembly'),
    id: z.string(),
    designator: z.string().optional(),
    ipn: BareIpnIdentifierRuntype.nullable().optional(),
    cpn: BareCpnIdentifierRuntype.nullable().optional(),
    children: z.array(CalculationNodeRiskBomItemsRuntype),
    calculated: RiskDataRuntype,
    parent: ParentRefRuntype,
});

export type CalculationNodeRiskAllAssemblies = z.infer<typeof CalculationNodeRiskAllAssembliesRuntype>;
export const CalculationNodeRiskAllAssembliesRuntype = z.object({
    type: z.literal('RiskAllAssemblies'),
    id: z.string(),
    children: z.array(CalculationNodeRiskAssemblyRuntype),
    calculated: RiskDataRuntype,
    parent: ParentRefRuntype,
});

export type CalculationNodeOffTheShelfPart = z.infer<typeof CalculationNodeOffTheShelfPartRuntype>;
export const CalculationNodeOffTheShelfPartRuntype = z.object({
    type: z.literal('OffTheShelfPart'),
    id: z.string(),
    part: CalculationNodeOffTheShelfPartPayloadRuntype,
    calculated: CalculatedRuntype,
    children: z.tuple([]),
    parent: ParentRefRuntype,
});

export type CalculationNodePartCategory = z.infer<typeof CalculationNodePartCategoryRuntype>;
export const CalculationNodePartCategoryRuntype = z.object({
    type: z.literal('PartCategory'),
    id: z.string(),
    name: z.string(),
    numberOfComponents: z.number(),
    calculated: CalculatedRuntype,
    get children(): z.ZodArray<
        z.ZodUnion<[typeof CalculationNodePartCategoryRuntype, typeof CalculationNodeOtsComponentRuntype]>
    > {
        return z.array(z.union([CalculationNodePartCategoryRuntype, CalculationNodeOtsComponentRuntype]));
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeBomItem = z.infer<typeof CalculationNodeBomItemRuntype>;
export const CalculationNodeBomItemRuntype = z.object({
    type: z.literal('BomItem'),
    id: z.string(),
    bomItem: BomItemsRuntype,
    designItems: z.array(DesignItemResponseDTONewRuntype),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(z.union([CalculationNodeOtsComponentRuntype, CalculationNodeOffTheShelfPartRuntype]));
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeManufacturer = z.infer<typeof CalculationNodeManufacturerRuntype>;
export const CalculationNodeManufacturerRuntype = z.object({
    type: z.literal('Manufacturer'),
    id: z.string(),
    name: z.string(),
    manufacturer: CalculationNodeManufacturerPayloadRuntype,
    calculated: CalculatedRuntype,
    countOfParts: z.number(),
    get children() {
        return z.array(CalculationNodeOffTheShelfPartRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeSupplierPart = z.infer<typeof CalculationNodeSupplierPartRuntype>;
export const CalculationNodeSupplierPartRuntype = z.object({
    type: z.literal('SupplierPart'),
    id: z.string(),
    supplier: SupplierDTORuntype,
    part: CalculationNodeOffTheShelfPartPayloadRuntype,
    calculated: CalculatedRuntype,
    children: z.tuple([]),
    parent: ParentRefRuntype,
});

export type CalculationNodeSupplierManufacturer = z.infer<typeof CalculationNodeSupplierManufacturerRuntype>;
export const CalculationNodeSupplierManufacturerRuntype = z.object({
    type: z.literal('SupplierManufacturer'),
    id: z.string(),
    name: z.string(),
    supplier: SupplierDTORuntype,
    manufacturer: CalculationNodeManufacturerPayloadRuntype,
    calculated: CalculatedRuntype,
    countOfParts: z.number(),
    get children() {
        return z.array(CalculationNodeSupplierPartRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeSupplier = z.infer<typeof CalculationNodeSupplierRuntype>;
export const CalculationNodeSupplierRuntype = z.object({
    type: z.literal('Supplier'),
    id: z.string(),
    supplier: SupplierDTORuntype,
    countOfManufacturers: z.number(),
    countOfParts: z.number(),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(CalculationNodeSupplierManufacturerRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeSupplierLibrary = z.infer<typeof CalculationNodeSupplierLibraryRuntype>;
export const CalculationNodeSupplierLibraryRuntype = z.object({
    type: z.literal('SupplierLibrary'),
    id: z.string(),
    name: z.string(),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(CalculationNodeSupplierRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodePartLibrary = z.infer<typeof CalculationNodePartLibraryRuntype>;
export const CalculationNodePartLibraryRuntype = z.object({
    type: z.literal('PartLibrary'),
    id: z.string(),
    name: z.string(),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(CalculationNodeManufacturerRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeComponentLibrary = z.infer<typeof CalculationNodeComponentLibraryRuntype>;
export const CalculationNodeComponentLibraryRuntype = z.object({
    type: z.literal('ComponentLibrary'),
    id: z.string(),
    name: z.string(),
    calculated: CalculatedRuntype,
    get children() {
        return z.array(CalculationNodePartCategoryRuntype);
    },
    parent: ParentRefRuntype,
});

export type CalculationNodeHistoricalOffer = z.infer<typeof CalculationNodeHistoricalOfferRuntype>;
export const CalculationNodeHistoricalOfferRuntype = z.object({
    type: z.literal('HistoricalOffer'),
    id: z.string(),
    offer: HistoricalOfferDTOSchema,
    calculated: CalculatedRuntype,
    children: z.tuple([]),
    parent: ParentRefRuntype,
});

export type CalculationNode =
    | CalculationNodeRiskAssembly
    | CalculationNodeRiskBomItems
    | CalculationNodeRiskOtsComponent
    | CalculationNodeRiskOtsPart
    | CalculationNodeRiskAllAssemblies
    // --------------------------
    | CalculationNodePartCategory
    | CalculationNodeOtsComponent
    | CalculationNodeCustomComponent
    | CalculationNodeOffTheShelfPart
    | CalculationNodeBomItem
    | CalculationNodeManufacturer
    | CalculationNodePartLibrary
    | CalculationNodeComponentLibrary
    | CalculationNodeHistoricalOffer
    | CalculationNodeSupplier
    | CalculationNodeSupplierManufacturer
    | CalculationNodeSupplierPart
    | CalculationNodeSupplierLibrary;

export const CalculationNodeRuntype: z.ZodType<CalculationNode> = z.discriminatedUnion('type', [
    CalculationNodeRiskAllAssembliesRuntype,
    CalculationNodeRiskAssemblyRuntype,
    CalculationNodeRiskOtsComponentRuntype,
    CalculationNodeRiskOtsPartRuntype,
    CalculationNodeRiskBomItemsRuntype,
    CalculationNodeOffTheShelfPartRuntype,
    CalculationNodePartCategoryRuntype,
    CalculationNodeOtsComponentRuntype,
    CalculationNodeCustomComponentRuntype,
    CalculationNodeBomItemRuntype,
    CalculationNodeManufacturerRuntype,
    CalculationNodePartLibraryRuntype,
    CalculationNodeComponentLibraryRuntype,
    CalculationNodeHistoricalOfferRuntype,
    CalculationNodeSupplierRuntype,
    CalculationNodeSupplierManufacturerRuntype,
    CalculationNodeSupplierPartRuntype,
    CalculationNodeSupplierLibraryRuntype,
]);

export type FormulaPerNode = z.infer<typeof FormulaPerNodeRuntype>;
export const FormulaPerNodeRuntype = z.object({
    for: z.union([CalculationNodeTypeRuntype, CalculationNodeIdRuntype]),
    attribute: z.string(),
    formula: z.string(),
});

export type TimeWindow = z.infer<typeof TimeWindowRuntype>;
export const TimeWindowRuntype = z
    .object({
        from: z.iso.date(),
        to: z.iso.date(),
    })
    .refine(({ from, to }) => from <= to, {
        error: '`from` date must be before or equal to `to` date',
        path: ['from', 'to'],
    });

export type FilterByType = z.infer<typeof FilterByTypeRuntype>;
export const FilterByTypeRuntype = z.union([
    z.literal('Component'),
    z.literal('OffTheShelfPart'),
    z.literal('PartCategory'),
    z.literal('Manufacturer'),
    z.literal('PartLibrary'),
    z.literal('ComponentLibrary'),
    z.literal('Supplier'),
    z.literal('SupplierLibrary'),
]);

export type RequestSearch = z.infer<typeof RequestSearchRuntype>;
export const RequestSearchRuntype = z.object({
    query: z.string(),
    filterByType: FilterByTypeRuntype.optional(),
});

const LispFormulaRuntype = z.object({
    syntax: z.literal('lisp'),
    formula: z.string(),
});

export type ExcelLikeV2Formula = z.infer<typeof ExcelLikeV2FormulaRuntype>;
const ExcelLikeV2FormulaRuntype = z.object({
    syntax: z.literal('excel-like-v2'),
    formula: z.object({
        formulas: z.array(FormulaPerNodeRuntype),
        rootNode: CalculationNodeTypeRuntype.optional(),
        cache: z.boolean().optional(),
    }),
});

export type StructuredAlertFormula = z.infer<typeof StructuredAlertFormulaRuntype>;
export const StructuredAlertFormulaRuntype = z.object({
    syntax: z.literal('structured:alert'),
    formula: z.object({
        message: z.string(),
        operator: z.enum(['>', '<']),
        attribute: z.string(),
        value: z.number(),
        for: z.array(CalculationNodeIdRuntype).optional(),
    }),
});

export type StructuredFeedFormula = z.infer<typeof StructuredFeedFormulaRuntype>;
export const StructuredFeedFormulaRuntype = z.object({
    syntax: z.literal('structured:feed'),
    formula: z.object({
        metric: z.string(),
        threshold: z.number(), // e.g., 0.05 for ±5%
        operator: z.enum(['relative-change', 'relative-increase', 'relative-decrease']), // Comparison operator: 'relative-change' compares abs(final/initial - 1) against threshold
        periods: z.number().int().min(1), // Number of weeks to look back (1 = 1 week, 4 = ~1 month, 12 = ~3 months)
        for: z.array(CalculationNodeTypeRuntype), // Node types this formula applies to
    }),
});

export type StructuredCorrelatedFeedFormula = z.infer<typeof StructuredCorrelatedFeedFormulaRuntype>;
export const StructuredCorrelatedFeedFormulaRuntype = z.object({
    syntax: z.literal('structured:correlated-feed'),
    formula: z.object({
        name: z.string(),
        periods: z.number().int().min(1),
        minConditions: z.number().int().min(1),
        conditions: z.array(
            z.object({
                metric: z.string(),
                operator: z.enum(['relative-change', 'relative-increase', 'relative-decrease']),
                threshold: z.number(),
            }),
        ),
        for: z.array(CalculationNodeTypeRuntype),
    }),
});

const NonComposeSyntaxRuntype = z.union([
    LispFormulaRuntype,
    ExcelLikeV2FormulaRuntype,
    StructuredAlertFormulaRuntype,
    StructuredFeedFormulaRuntype,
    StructuredCorrelatedFeedFormulaRuntype,
]);

export type FormulaSyntax = z.infer<typeof FormulaSyntaxRuntype>;
export const FormulaSyntaxRuntype = z.union([
    NonComposeSyntaxRuntype,
    z.object({
        syntax: z.literal('pipeline'),
        formula: z.array(NonComposeSyntaxRuntype),
    }),
]);

export type RequestFetchCalculation = z.infer<typeof RequestFetchCalculationRuntype>;
export const RequestFetchCalculationRuntype = z.object({
    root: CalculationNodeIdRuntype,
    timeWindow: TimeWindowRuntype,
    formula: FormulaSyntaxRuntype,
    skipCache: z.boolean().optional().default(false),
});

export function splitIntoWeeks(timeWindow: TimeWindow): string[] {
    const weeks: string[] = [];
    const start = new Date(timeWindow.from);
    // Snap back to the Monday that begins this week so these dates match the SQL
    // `time_bucket('1 week', …)` origin (Timescale's default is Monday 2000-01-03).
    // Every weekly series shares this phase; the formula engine aligns series by
    // date and resamples a cross-phase combine, so keeping this consistent avoids
    // needless resampling.
    start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
    const end = new Date(timeWindow.to);
    // oxlint-disable-next-line no-unmodified-loop-condition -- Date objects are mutated via setUTCDate
    while (start < end) {
        weeks.push(start.toISOString().split('T')[0]);
        start.setUTCDate(start.getUTCDate() + 7);
    }
    return weeks;
}

const SearchResultBaseRuntype = z.object({ matchScore: z.number().optional().default(0) });

export type SearchResultOtsComponent = z.infer<typeof SearchResultOtsComponentRuntype>;
export const SearchResultOtsComponentRuntype = CalculationNodeOtsComponentRuntype.pick({
    type: true,
    id: true,
    component: true,
    countOfParts: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultCustomComponent = z.infer<typeof SearchResultCustomComponentRuntype>;
export const SearchResultCustomComponentRuntype = CalculationNodeCustomComponentRuntype.pick({
    type: true,
    id: true,
    component: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultPartCategory = z.infer<typeof SearchResultPartCategoryRuntype>;
export const SearchResultPartCategoryRuntype = CalculationNodePartCategoryRuntype.pick({
    type: true,
    id: true,
    name: true,
    numberOfComponents: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultOffTheShelfPart = z.infer<typeof SearchResultOffTheShelfPartRuntype>;
export const SearchResultOffTheShelfPartRuntype = CalculationNodeOffTheShelfPartRuntype.pick({
    type: true,
    id: true,
    part: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultManufacturer = z.infer<typeof SearchResultManufacturerRuntype>;
export const SearchResultManufacturerRuntype = CalculationNodeManufacturerRuntype.pick({
    type: true,
    id: true,
    name: true,
    countOfParts: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultPartLibrary = z.infer<typeof SearchResultPartLibraryRuntype>;
export const SearchResultPartLibraryRuntype = CalculationNodePartLibraryRuntype.pick({
    type: true,
    id: true,
    name: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultComponentLibrary = z.infer<typeof SearchResultComponentLibraryRuntype>;
export const SearchResultComponentLibraryRuntype = CalculationNodeComponentLibraryRuntype.pick({
    type: true,
    id: true,
    name: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultSupplier = z.infer<typeof SearchResultSupplierRuntype>;
export const SearchResultSupplierRuntype = CalculationNodeSupplierRuntype.pick({
    type: true,
    id: true,
    supplier: true,
}).extend(SearchResultBaseRuntype.shape);

export type SearchResultSupplierLibrary = z.infer<typeof SearchResultSupplierLibraryRuntype>;
export const SearchResultSupplierLibraryRuntype = CalculationNodeSupplierLibraryRuntype.pick({
    type: true,
    id: true,
    name: true,
}).extend(SearchResultBaseRuntype.shape);

// Search result types
// Every search result type needs to have a CalculationNodeId and a bit of extra information
// to render the search result preview in the UI.
export type CalculationSearchResult = z.infer<typeof CalculationSearchResultRuntype>;
export const CalculationSearchResultRuntype = z.discriminatedUnion('type', [
    SearchResultPartCategoryRuntype,
    SearchResultOtsComponentRuntype,
    SearchResultCustomComponentRuntype,
    SearchResultOffTheShelfPartRuntype,
    SearchResultManufacturerRuntype,
    SearchResultPartLibraryRuntype,
    SearchResultComponentLibraryRuntype,
    SearchResultSupplierRuntype,
    SearchResultSupplierLibraryRuntype,
]);

export const PriceAttributeRuntype = z.enum([
    'unit_price_1',
    'unit_price_100',
    'unit_price_1k',
    'unit_price_10k',
    'unit_price_50k',
]);

export type PriceAttribute = z.infer<typeof PriceAttributeRuntype>;

export const OfferPriceTypeRuntype = z.enum([
    'list_price',
    'contract_price',
    'target_price',
    'quote_price',
    'purchase_price',
]);

export type OfferPriceType = z.infer<typeof OfferPriceTypeRuntype>;

export const MONITORING_INDEX_OFFER_PRICE_TYPES = [
    'list_price',
    'contract_price',
] as const satisfies readonly OfferPriceType[];

export const OfferContributorFilterRuntype = z.object({
    priceTypes: z.array(OfferPriceTypeRuntype).optional(),
});

export type OfferContributorFilter = z.infer<typeof OfferContributorFilterRuntype>;

export const OfferSortAttributeRuntype = z.enum([
    'time',
    'unit_price_1',
    'unit_price_100',
    'unit_price_1k',
    'unit_price_10k',
    'unit_price_50k',
    'total_stock',
    'lead_time_days',
    'origin',
    'price_type',
    'original_currency',
    'packaging',
]);

export type OfferSortAttribute = z.infer<typeof OfferSortAttributeRuntype>;

export const OfferSortRuntype = z.object({
    attribute: OfferSortAttributeRuntype,
    direction: z.enum(['asc', 'desc']),
});

export type OfferSort = z.infer<typeof OfferSortRuntype>;

export const RequestFetchOffersRuntype = z.object({
    timeWindow: TimeWindowRuntype,
    partIds: z.array(z.string()),
    offset: z.number().int().min(0).optional().default(0),
    limit: z.number().int().min(1).max(1000).optional().default(100),
    sort: OfferSortRuntype.optional(),
    /**
     * When set, excludes offers where this price attribute is NULL.
     * Use this when the UI is displaying a specific price column to avoid showing
     * misleading "€0.00" values for offers that don't have that price break.
     */
    filterNonNullPriceAttribute: PriceAttributeRuntype.optional(),
    contributorFilter: OfferContributorFilterRuntype.optional(),
});

export type RequestFetchOffers = z.input<typeof RequestFetchOffersRuntype>;

export const StaticFormulaRuntype = z.object({
    formula: FormulaSyntaxRuntype,
    selectors: z.array(
        z.object({
            type: CalculationNodeTypeRuntype,
            id: NullAsUndefinedRuntype(z.string()),
        }),
    ),
});
export type StaticFormula = z.infer<typeof StaticFormulaRuntype>;

export const DbFormulaRuntype = StaticFormulaRuntype.extend({
    id: z.uuid(),
    label: z.string().optional().default(''),
    description: z.string().optional().default(''),
    createdAt: z.date(),
    action: z.enum(['upsert', 'delete']).optional(),
});
export type DbFormula = z.infer<typeof DbFormulaRuntype>;

export const FormulaDocumentRuntype = z.union([StaticFormulaRuntype, DbFormulaRuntype]);
export type FormulaDocument = z.infer<typeof FormulaDocumentRuntype>;

function NullAsUndefinedRuntype<T>(schema: z.ZodType<T>): z.ZodType<T | undefined> {
    return schema
        .nullable()
        .optional()
        .transform((value) => value ?? undefined);
}

// ============================================
// Monitoring Favourites
// ============================================

export const MonitoringFavouriteTypeRuntype = z.enum(['node', 'comparison']);
export type MonitoringFavouriteType = z.infer<typeof MonitoringFavouriteTypeRuntype>;

export const NodeFavouriteDataRuntype = z.object({
    nodeId: CalculationNodeIdRuntype,
});
export type NodeFavouriteData = z.infer<typeof NodeFavouriteDataRuntype>;

export const ComparisonFavouriteDataRuntype = z.object({
    nodeIds: z.array(CalculationNodeIdRuntype),
    dimensionId: z.string(),
});
export type ComparisonFavouriteData = z.infer<typeof ComparisonFavouriteDataRuntype>;

const MonitoringFavouriteBase = z.object({
    id: z.string().uuid(),
    label: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});

export const MonitoringFavouriteRuntype = z.discriminatedUnion('type', [
    MonitoringFavouriteBase.extend({
        type: z.literal('node'),
        data: NodeFavouriteDataRuntype,
    }),
    MonitoringFavouriteBase.extend({
        type: z.literal('comparison'),
        data: ComparisonFavouriteDataRuntype,
    }),
]);
export type MonitoringFavourite = z.infer<typeof MonitoringFavouriteRuntype>;

// ============================================
// Monitoring Charts
// ============================================

export const MonitoringChartTimeWindowRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('relative'),
        value: z.enum(['all', 'year', 'ytd', '6months', '3months', '1month', '10days']),
    }),
    z.object({ type: z.literal('absolute'), value: TimeWindowRuntype }),
]);
export type MonitoringChartTimeWindow = z.infer<typeof MonitoringChartTimeWindowRuntype>;

export const MonitoringChartTimeSeriesRuntype = z.object({
    nodeId: CalculationNodeIdRuntype,
    attribute: z.string(),
    visible: z.boolean().optional().default(true),
    nodeLabel: z.string().optional(),
});
export type MonitoringChartTimeSeries = z.infer<typeof MonitoringChartTimeSeriesRuntype>;

export const MonitoringChartConfigV1Runtype = z.object({
    version: z.literal(1),
    visibleTimeWindow: MonitoringChartTimeWindowRuntype,
    selectedTimeWindow: TimeWindowRuntype.optional(),
    scalingMode: z.enum(['absolute', 'relative']).optional(),
    timeSeries: z.array(MonitoringChartTimeSeriesRuntype),
});
export type MonitoringChartConfigV1 = z.infer<typeof MonitoringChartConfigV1Runtype>;

export const MonitoringChartDatasetBindingRuntype = z.object({
    datasetId: z.string().trim().min(1),
    dimensions: z.array(z.string().trim().min(1)).min(1).optional(),
});
export type MonitoringChartDatasetBinding = z.infer<typeof MonitoringChartDatasetBindingRuntype>;

export const MonitoringChartAgentDatasetRuntype = z.object({
    id: z.string().trim().min(1),
    dimensions: z.array(z.string().trim().min(1)).min(1),
    source: z.array(z.unknown()),
    query: z.string().trim().min(1).optional(),
});
export type MonitoringChartAgentDataset = z.infer<typeof MonitoringChartAgentDatasetRuntype>;

export const MonitoringChartConfigV2Runtype = z.object({
    version: z.literal(2),
    kind: z.literal('agent-vega-lite'),
    spec: z.record(z.string(), z.unknown()),
    datasets: z.array(MonitoringChartDatasetBindingRuntype).min(1),
    datasetSources: z.array(MonitoringChartAgentDatasetRuntype).min(1),
});
export type MonitoringChartConfigV2 = z.infer<typeof MonitoringChartConfigV2Runtype>;

export const MonitoringChartConfigRuntype = z.discriminatedUnion('version', [
    MonitoringChartConfigV1Runtype,
    MonitoringChartConfigV2Runtype,
]);
export type MonitoringChartConfig = z.infer<typeof MonitoringChartConfigRuntype>;

export const MonitoringChartRuntype = z.object({
    id: z.string().uuid(),
    title: z.string(),
    config: MonitoringChartConfigRuntype,
    createdBy: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export type MonitoringChart = z.infer<typeof MonitoringChartRuntype>;

export const MonitoringChartCreateInputRuntype = z.object({
    title: z.string().min(1),
    config: MonitoringChartConfigRuntype,
});
export type MonitoringChartCreateInput = z.infer<typeof MonitoringChartCreateInputRuntype>;

export const MonitoringChartUpdateInputRuntype = z.object({
    id: z.string().uuid(),
    title: z.string().min(1),
    config: MonitoringChartConfigRuntype,
});
export type MonitoringChartUpdateInput = z.infer<typeof MonitoringChartUpdateInputRuntype>;

// ============================================
// Monitoring Dashboards
// ============================================

const POSTGRES_INT_MAX = 2_147_483_647;
const MonitoringDashboardGridCoordinateRuntype = z.int().min(0).max(POSTGRES_INT_MAX);
const MonitoringDashboardGridSizeRuntype = z.int().min(1).max(POSTGRES_INT_MAX);

export const MonitoringDashboardChartLayoutRuntype = z.object({
    x: MonitoringDashboardGridCoordinateRuntype,
    y: MonitoringDashboardGridCoordinateRuntype,
    w: MonitoringDashboardGridSizeRuntype,
    h: MonitoringDashboardGridSizeRuntype,
});
export type MonitoringDashboardChartLayout = z.infer<typeof MonitoringDashboardChartLayoutRuntype>;

export const MonitoringDashboardChartRuntype = z.object({
    chartId: z.string().uuid(),
    layout: MonitoringDashboardChartLayoutRuntype,
});
export type MonitoringDashboardChart = z.infer<typeof MonitoringDashboardChartRuntype>;

export const MonitoringDashboardConfigV1Runtype = z.object({
    version: z.literal(1),
    visibleTimeWindow: MonitoringChartTimeWindowRuntype.optional(),
    charts: z
        .array(MonitoringDashboardChartRuntype)
        .refine(
            (charts) => new Set(charts.map((chart) => chart.chartId)).size === charts.length,
            'A dashboard cannot contain the same chart more than once',
        ),
});
export type MonitoringDashboardConfigV1 = z.infer<typeof MonitoringDashboardConfigV1Runtype>;

export const MonitoringDashboardConfigRuntype = MonitoringDashboardConfigV1Runtype;
export type MonitoringDashboardConfig = z.infer<typeof MonitoringDashboardConfigRuntype>;

export const MonitoringDashboardRuntype = z.object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    config: MonitoringDashboardConfigRuntype,
    createdBy: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export type MonitoringDashboard = z.infer<typeof MonitoringDashboardRuntype>;

export const MonitoringDashboardCreateInputRuntype = z.object({
    title: z.string().min(1),
    description: z.string().nullable().optional(),
    config: MonitoringDashboardConfigRuntype,
});
export type MonitoringDashboardCreateInput = z.infer<typeof MonitoringDashboardCreateInputRuntype>;

export const MonitoringDashboardUpdateInputRuntype = z.object({
    id: z.string().uuid(),
    title: z.string().min(1),
    description: z.string().nullable().optional(),
    config: MonitoringDashboardConfigRuntype,
});
export type MonitoringDashboardUpdateInput = z.infer<typeof MonitoringDashboardUpdateInputRuntype>;

// ============================================
// Monitoring Metrics
// ============================================

/** Stable identities for code-defined persisted monitoring metrics. */
export const BuiltInMetricIdRuntype = z.enum([
    'off_the_shelf_part.contract_price_index',
    'off_the_shelf_part.count_offers',
    'off_the_shelf_part.lead_time_index_count',
    'off_the_shelf_part.lead_time_index_days',
    'off_the_shelf_part.market_unit_price_1k_p50',
    'off_the_shelf_part.price_change_count',
    'off_the_shelf_part.price_change_log_sum',
    'off_the_shelf_part.purchase_price_index',
    'off_the_shelf_part.quote_price_index',
    'off_the_shelf_part.stock_index',
    'ots_component.contract_price_index',
    'ots_component.count_offers',
    'ots_component.count_supplier_market_price_offers',
    'ots_component.market_price_p0',
    'ots_component.min_purchase_price',
    'ots_component.demand_quantity',
    'ots_component.demand_to_market_stock_ratio',
    'ots_component.latest_standard_price',
    'ots_component.lead_time_index_part_count',
    'ots_component.lead_time_index_part_days',
    'ots_component.lead_time_max_days',
    'ots_component.market_lead_time_p50_days',
    'ots_component.market_price_p10',
    'ots_component.market_stock_p50',
    'ots_component.market_stock_total',
    'ots_component.market_unit_price_1k_p50',
    'ots_component.market_price_p50',
    'ots_component.price_change_count',
    'ots_component.price_change_log_sum',
    'ots_component.purchase_price_index',
    'ots_component.quote_price_index',
    'ots_component.projected_spend_cheapest_purchase_price',
    'ots_component.projected_spend_last_purchase_price',
    'ots_component.projected_spend_last_standard_price',
    'ots_component.projected_spend_p0_1k',
    'ots_component.projected_spend_p10_1k',
    'ots_component.projected_spend_p50_1k',
    'ots_component.stock_index',
    'ots_component.supplier_origin_offer_count',
    'ots_component.supplier_stock_concentration_all',
    'ots_component.supplier_stock_concentration_distributors',
    'ots_component.supplier_stock_concentration_manufacturers',
    'ots_component.unique_supplier_count',
]);
export type BuiltInMetricId = z.infer<typeof BuiltInMetricIdRuntype>;

export const MetricDtoRuntype = z.object({
    id: BuiltInMetricIdRuntype,
    entityType: z.string(),
    entityTable: z.string(),
    metricName: z.string(),
});
export type MetricDto = z.infer<typeof MetricDtoRuntype>;

const MetricExecutionPlanTimestampRuntype = z.iso.datetime();
const MetricDateRuntype = z.iso.date();

export const MetricEntityTypeRuntype = z.enum(['OtsComponent', 'OffTheShelfPart']);
export type MetricEntityType = z.infer<typeof MetricEntityTypeRuntype>;

export const MetricExecutionPlanStageRuntype = z.enum(['drafting', 'reviewing', 'fixing', 'ready', 'failed']);
export type MetricExecutionPlanStage = z.infer<typeof MetricExecutionPlanStageRuntype>;

const MetricSqlExecutionPlanRuntype = z.object({
    kind: z.literal('sql'),
    sql: z.string().trim().min(1),
});

const MetricMaterializedViewExecutionPlanRuntype = z.object({
    kind: z.literal('materialized_view'),
    name: z.string().trim().min(1),
    refreshStrategy: z.string().trim().min(1).optional(),
    systemDefined: z.literal(true),
});

export const MetricReadyExecutionPlanRuntype = z.discriminatedUnion('kind', [
    MetricSqlExecutionPlanRuntype,
    MetricMaterializedViewExecutionPlanRuntype,
]);
export type MetricReadyExecutionPlan = z.infer<typeof MetricReadyExecutionPlanRuntype>;

const MetricWritableReadyExecutionPlanRuntype = MetricSqlExecutionPlanRuntype;

export const MetricPlanReviewRuntype = z.object({
    status: z.enum(['passed', 'failed']),
    checks: z.array(
        z.object({
            id: z.string().trim().min(1),
            status: z.enum(['passed', 'failed']),
            message: z.string().trim().min(1).optional(),
        }),
    ),
    reviewedAt: MetricExecutionPlanTimestampRuntype,
});
export type MetricPlanReview = z.infer<typeof MetricPlanReviewRuntype>;

const MetricDraftingExecutionPlanRuntype = z.object({
    stage: z.literal('drafting'),
    candidate: MetricReadyExecutionPlanRuntype.optional(),
    updatedAt: MetricExecutionPlanTimestampRuntype,
});

const MetricReviewingExecutionPlanRuntype = z.object({
    stage: z.literal('reviewing'),
    candidate: MetricReadyExecutionPlanRuntype,
    updatedAt: MetricExecutionPlanTimestampRuntype,
});

const MetricFixingExecutionPlanRuntype = z.object({
    stage: z.literal('fixing'),
    candidate: MetricReadyExecutionPlanRuntype,
    review: MetricPlanReviewRuntype,
    updatedAt: MetricExecutionPlanTimestampRuntype,
});

const MetricReadyExecutionPlanStateRuntype = z.object({
    stage: z.literal('ready'),
    plan: MetricReadyExecutionPlanRuntype,
    review: MetricPlanReviewRuntype,
    readyAt: MetricExecutionPlanTimestampRuntype,
});

const MetricFailedExecutionPlanRuntype = z.object({
    stage: z.literal('failed'),
    candidate: MetricReadyExecutionPlanRuntype.optional(),
    review: MetricPlanReviewRuntype.optional(),
    errorCode: z.string().trim().min(1),
    errorMessage: z.string().trim().min(1),
    failedAt: MetricExecutionPlanTimestampRuntype,
});

const MetricWritableDraftingExecutionPlanRuntype = MetricDraftingExecutionPlanRuntype.extend({
    candidate: MetricWritableReadyExecutionPlanRuntype.optional(),
});

const MetricWritableReviewingExecutionPlanRuntype = MetricReviewingExecutionPlanRuntype.extend({
    candidate: MetricWritableReadyExecutionPlanRuntype,
});

const MetricWritableFixingExecutionPlanRuntype = MetricFixingExecutionPlanRuntype.extend({
    candidate: MetricWritableReadyExecutionPlanRuntype,
});

const MetricWritableFailedExecutionPlanRuntype = MetricFailedExecutionPlanRuntype.extend({
    candidate: MetricWritableReadyExecutionPlanRuntype.optional(),
});

export const MetricExecutionPlanRuntype = z.discriminatedUnion('stage', [
    MetricDraftingExecutionPlanRuntype,
    MetricReviewingExecutionPlanRuntype,
    MetricFixingExecutionPlanRuntype,
    MetricReadyExecutionPlanStateRuntype,
    MetricFailedExecutionPlanRuntype,
]);
export type MetricExecutionPlan = z.infer<typeof MetricExecutionPlanRuntype>;

export const MetricWritableExecutionPlanRuntype = z.discriminatedUnion('stage', [
    MetricWritableDraftingExecutionPlanRuntype,
    MetricWritableReviewingExecutionPlanRuntype,
    MetricWritableFixingExecutionPlanRuntype,
    MetricWritableFailedExecutionPlanRuntype,
]);
export type MetricWritableExecutionPlan = z.infer<typeof MetricWritableExecutionPlanRuntype>;

export const MetricRuntype = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1),
    description: z.string().trim().min(1),
    entityType: MetricEntityTypeRuntype,
    executionPlan: MetricExecutionPlanRuntype,
    createdAt: z.date(),
    updatedAt: z.date(),
});
export type Metric = z.infer<typeof MetricRuntype>;

export const MetricCreateInputRuntype = z.object({
    name: z.string().trim().min(1),
    description: z.string().trim().min(1),
    entityType: MetricEntityTypeRuntype,
    executionPlan: MetricWritableExecutionPlanRuntype,
});
export type MetricCreateInput = z.infer<typeof MetricCreateInputRuntype>;

export const MetricListInputRuntype = z.object({
    query: z.string().trim().min(1).optional(),
    entityType: MetricEntityTypeRuntype.optional(),
    stage: MetricExecutionPlanStageRuntype.optional(),
});
export type MetricListInput = z.infer<typeof MetricListInputRuntype>;

export const MetricGetInputRuntype = z.object({
    id: z.string().uuid(),
});
export type MetricGetInput = z.infer<typeof MetricGetInputRuntype>;

export const MetricUpdateInputRuntype = z.object({
    id: z.string().uuid(),
    executionPlan: MetricWritableExecutionPlanRuntype,
});
export type MetricUpdateInput = z.infer<typeof MetricUpdateInputRuntype>;

export const MetricDeleteInputRuntype = z.object({
    id: z.string().uuid(),
});
export type MetricDeleteInput = z.infer<typeof MetricDeleteInputRuntype>;

export const MetricTimeWindowRuntype = z
    .object({
        from: MetricDateRuntype,
        to: MetricDateRuntype,
    })
    .refine(({ from, to }) => from <= to, {
        error: '`from` date must be before or equal to `to` date',
        path: ['from', 'to'],
    });
export type MetricTimeWindow = z.infer<typeof MetricTimeWindowRuntype>;

export const MetricSnapshotRuntype = z.object({
    id: z.string().uuid(),
    metricId: z.string().uuid(),
    timeWindow: MetricTimeWindowRuntype,
    granularityDays: z.literal(7),
    cacheKey: z.string().trim().min(1),
    status: z.enum(['running', 'succeeded', 'failed', 'cancelled']),
    progress: z.enum(['checking_cache', 'executing_sql', 'writing_cache', 'completed']),
    cachePath: z.string().trim().min(1).nullable(),
    computedAt: z.date().nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    warnings: z.array(z.string()),
    errorCode: z.string().trim().min(1).nullable(),
    errorMessage: z.string().trim().min(1).nullable(),
    createdAt: z.date(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
});
export type MetricSnapshot = z.infer<typeof MetricSnapshotRuntype>;

export const MetricRunInputRuntype = z.object({
    metricId: z.string().uuid(),
    timeWindow: MetricTimeWindowRuntype,
    granularityDays: z.literal(7).default(7),
    waitForCompletion: z.boolean().optional().default(false),
});
export type MetricRunInput = {
    metricId: string;
    timeWindow: MetricTimeWindow;
    granularityDays?: 7;
    waitForCompletion?: boolean;
};

export const MetricExecutionResultRuntype = z.object({
    rows: z.array(z.record(z.string(), z.unknown())),
    rowCount: z.number().int().nonnegative(),
    computedAt: MetricExecutionPlanTimestampRuntype,
});
export type MetricExecutionResult = z.infer<typeof MetricExecutionResultRuntype>;

export const MetricRunResultRuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('metric_not_ready'),
        metric: MetricRuntype.pick({ id: true, executionPlan: true }),
    }),
    z.object({
        kind: z.literal('cache_hit'),
        snapshot: MetricSnapshotRuntype,
        result: MetricExecutionResultRuntype,
    }),
    z.object({
        kind: z.literal('scheduled'),
        snapshot: MetricSnapshotRuntype,
        job: z.object({
            computation: z.string(),
            period: z.string(),
            status: z
                .object({
                    state: z.enum(['pending', 'running', 'succeeded', 'failed']),
                    progress: z.number(),
                    shardsDone: z.number().int().nonnegative(),
                    shardsTotal: z.number().int().nonnegative(),
                    shardsDead: z.number().int().nonnegative(),
                    version: z.number().int().positive(),
                    lastError: z.string().nullable(),
                })
                .optional(),
        }),
    }),
    z.object({
        kind: z.literal('completed'),
        snapshot: MetricSnapshotRuntype,
        result: MetricExecutionResultRuntype,
    }),
    z.object({
        kind: z.literal('failed'),
        snapshot: MetricSnapshotRuntype,
        errorCode: z.string().trim().min(1).optional(),
        errorMessage: z.string().trim().min(1).optional(),
    }),
]);
export type MetricRunResult = z.infer<typeof MetricRunResultRuntype>;

// ============================================
// Onboarding
// ============================================

export type OnboardingStatus = z.infer<typeof OnboardingStatusRuntype>;
export const OnboardingStatusRuntype = z.object({
    apiKeysConfigured: z.number(),
    componentsImported: z.number(),
    demandsUploadedCount: z.number(),
    offersImportedCount: z.number(),
    partCategoriesCount: z.number(),
});

export const PriceBreakRuntype = z.enum(['1k', '10k', '50k']);
export type PriceBreak = z.infer<typeof PriceBreakRuntype>;

export type RequestQueryCalculationNodes = z.infer<typeof RequestQueryCalculationNodesRuntype>;
export const RequestQueryCalculationNodesRuntype = z.object({
    parent: CalculationNodeIdRuntype,
    from: z.int().min(0),
    size: z.int().min(1).max(100),
    sortBy: z.object({
        field: z.string(),
        direction: z.enum(['asc', 'desc']),
    }),
});

export type ResponseQueryCalculationNodes = z.infer<typeof ResponseQueryCalculationNodesRuntype>;
export const ResponseQueryCalculationNodesRuntype = z.object({
    nodes: z.array(CalculationNodeRuntype),
    total: z.int().min(0),
});

// ============================================
// Feed Types
// ============================================

export const FeedItemKindRuntype = z.enum(['feed', 'alert', 'correlated_feed']);
export type FeedItemKind = z.infer<typeof FeedItemKindRuntype>;

export const FeedItemAttributeRuntype = z.string();
export type FeedItemAttribute = z.infer<typeof FeedItemAttributeRuntype>;

export const FeedItemRuntype = z.object({
    id: z.uuid(),
    idempotencyKey: z.string(),
    node: CalculationNodeIdRuntype,
    parent: CalculationNodeIdRuntype.nullable(),
    kind: FeedItemKindRuntype.default('feed'),
    attributes: z.array(FeedItemAttributeRuntype).default([]),
    metricType: z.string(),
    value: z.number(),
    windowStart: z.date().nullable().default(null),
    windowEnd: z.date().nullable().default(null),
    detectedAt: z.date(),
    createdAt: z.date(),
    viewedAt: z.date().nullable(),
    message: z.string().nullable().default(null),
});
export type FeedItem = z.infer<typeof FeedItemRuntype>;

export const FeedItemDtoRuntype = FeedItemRuntype.extend({
    calculationNode: CalculationNodeRuntype.nullable(),
});
export type FeedItemDto = z.infer<typeof FeedItemDtoRuntype>;

export const FeedDisplayCalculationNodeRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('OtsComponent'),
        id: z.string(),
        component: z.object({
            id: z.string(),
            category: z.string().nullable().optional(),
        }),
    }),
    z.object({
        type: z.literal('CustomComponent'),
        id: z.string(),
        component: z.object({
            id: z.string(),
            description: z.string().nullable().optional(),
        }),
    }),
    z.object({
        type: z.literal('BomItem'),
        id: z.string(),
        designItems: z.array(
            z.object({
                designator: z.string().nullable(),
            }),
        ),
    }),
    z.object({
        type: z.literal('PartCategory'),
        id: z.string(),
        name: z.string(),
        numberOfComponents: z.number(),
    }),
    z.object({
        type: z.literal('OffTheShelfPart'),
        id: z.string(),
        part: z.object({
            manufacturer: z.object({
                name: z.string(),
            }),
            mpn: z.string(),
            description: z.string().nullable().optional(),
        }),
    }),
    z.object({
        type: z.literal('Manufacturer'),
        id: z.string(),
        name: z.string(),
    }),
    z.object({
        type: z.literal('PartLibrary'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('ComponentLibrary'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('HistoricalOffer'),
        id: z.string(),
        offer: z.object({
            stock_location: z.object({
                supplier: z.object({
                    name: z.string(),
                }),
            }),
        }),
    }),
    z.object({
        type: z.literal('Supplier'),
        id: z.string(),
        supplier: z.object({
            name: z.string(),
        }),
    }),
    z.object({
        type: z.literal('SupplierLibrary'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('RiskAllAssemblies'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('RiskAssembly'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('RiskOtsComponent'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('RiskOtsPart'),
        id: z.string(),
    }),
    z.object({
        type: z.literal('RiskBomItems'),
        id: z.string(),
    }),
]);
export type FeedDisplayCalculationNode = z.infer<typeof FeedDisplayCalculationNodeRuntype>;

export const FeedListItemDtoRuntype = FeedItemRuntype.extend({
    calculationNode: FeedDisplayCalculationNodeRuntype.nullable(),
});
export type FeedListItemDto = z.infer<typeof FeedListItemDtoRuntype>;

export const FeedResponseRuntype = z.object({
    items: z.array(FeedItemDtoRuntype),
    total: z.number().int().min(0),
});
export type FeedResponse = z.infer<typeof FeedResponseRuntype>;

export const FeedListResponseRuntype = z.object({
    items: z.array(FeedListItemDtoRuntype),
    total: z.number().int().min(0),
});
export type FeedListResponse = z.infer<typeof FeedListResponseRuntype>;

export const CreateFeedItemInputSchema = z.object({
    node: CalculationNodeIdRuntype,
    parent: CalculationNodeIdRuntype.nullable().optional(),
    kind: FeedItemKindRuntype.optional(),
    attributes: z.array(FeedItemAttributeRuntype).optional(),
    metricType: z.string(),
    value: z.number(),
    windowStart: z.date().optional(),
    windowEnd: z.date().optional(),
    detectedAt: z.date(),
    message: z.string().optional(),
});
export type CreateFeedItemInput = z.infer<typeof CreateFeedItemInputSchema>;

export const GetFeedInputSchema = z.object({
    sinceHours: z.number().int().min(1).default(24),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).default(0),
});
export type GetFeedInput = z.infer<typeof GetFeedInputSchema>;

export const GetChildrenInputSchema = z.object({
    parentNodeId: z.string(),
    metricType: z.string().optional(),
    sinceHours: z.number().int().min(1).default(24),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).default(0),
});
export type GetChildrenInput = z.infer<typeof GetChildrenInputSchema>;

export const GetDigestInputSchema = z.object({
    weekStart: z.date(),
    weekEnd: z.date(),
    limit: z.number().int().min(1).max(250).default(20),
    offset: z.number().int().min(0).default(0),
});
export type GetDigestInput = z.infer<typeof GetDigestInputSchema>;

export const DeleteFeedItemInputSchema = z.object({
    id: z.uuid(),
});
export type DeleteFeedItemInput = z.infer<typeof DeleteFeedItemInputSchema>;

export const GetAllFeedItemsInputSchema = z.object({
    sinceHours: z.number().int().min(1).default(24),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).default(0),
    node: CalculationNodeIdRuntype.optional(),
    metricType: z.string().optional(),
    metricTypePrefix: z.string().optional(),
    kind: FeedItemKindRuntype.optional(),
    /** When set, returns feed items for this node and up to 3 levels of descendants. */
    subtreeNode: CalculationNodeIdRuntype.optional(),
});
export type GetAllFeedItemsInput = z.infer<typeof GetAllFeedItemsInputSchema>;

export const GetTopPartCategoryFeedMatrixInputSchema = z.void();
export type GetTopPartCategoryFeedMatrixInput = z.infer<typeof GetTopPartCategoryFeedMatrixInputSchema>;

export const TopPartCategoryFeedMatrixRowSchema = z.object({
    category: CalculationNodePartCategoryRuntype,
    items: z.array(FeedListItemDtoRuntype),
});
export type TopPartCategoryFeedMatrixRow = z.infer<typeof TopPartCategoryFeedMatrixRowSchema>;

export const TopPartCategoryFeedMatrixResponseSchema = z.object({
    rows: z.array(TopPartCategoryFeedMatrixRowSchema),
});
export type TopPartCategoryFeedMatrixResponse = z.infer<typeof TopPartCategoryFeedMatrixResponseSchema>;

export const GetMetricTypesInputSchema = z.object({
    sinceHours: z.number().int().min(1).default(168), // 1 week default
});
export type GetMetricTypesInput = z.infer<typeof GetMetricTypesInputSchema>;

// ============================================
// Indexing Feature Flags
// ============================================

export type IndexingFeatureFlags = z.infer<typeof IndexingFeatureFlagsSchema>;
export const IndexingFeatureFlagsSchema = z.object({
    /**
     * Whether to enable the LRU cache for the indexing service.
     */
    enableLruCache: z.boolean().optional().default(false),
    /**
     * Whether to enable the indexing of the SupplierLibrary root.
     */
    enableIndexingOfSupplierLibrary: z.boolean().optional().default(false),
    /**
     * Whether to enable the indexing of the RiskAllAssemblies root.
     */
    enableIndexingOfRiskAllAssemblies: z.boolean().optional().default(false),
});

/**
 * Returns the names of feature flags that are enabled (true).
 */
export function getEnabledFlagNames(flags: IndexingFeatureFlags | undefined): string[] {
    if (!flags) return [];
    return Object.entries(flags)
        .filter(([, value]) => value === true)
        .map(([key]) => key);
}
